import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
} from "react";

import { Grid } from "@mui/material";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { VscWarning } from "react-icons/vsc";

import ConfirmationModal from "../../../../../../../../components/Dialogs/ConfirmationModal";
import { Color } from "../../../../../../../../components/Form/Button";
import useStyles from "./styles";
import { CommodityCode } from "../../../../../../../../models/commodity.code";
import { Status } from "../../../../../../../../util/enums";

interface SwitchWarningModalProps {
  onConfirm: (id: number) => void;
}

export interface SwitchWarningModalHandles {
  open: (code: CommodityCode) => void;
  close: () => void;
}

const SwitchWarningModal: ForwardRefRenderFunction<SwitchWarningModalHandles, SwitchWarningModalProps> = (
  props,
  ref
) => {
  const { onConfirm } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [code, setCode] = useState<CommodityCode>();
  const [modalOpened, setModalOpened] = useState<boolean>(false);

  const isInactivating = code?.status === Status.ACTIVE;

  const onOpenModal = useCallback(
    (code: CommodityCode) => {
      setCode(code);
      setModalOpened(true)
    }, []);

  const onCloseModal = useCallback(() => setModalOpened(false), []);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  const onSubmitModal = () => {
    if (!code || !code.id) return;

    onConfirm(code.id);
    setModalOpened(false);
  };

  const confirmAction = isInactivating ? translate("inactivate") : translate("activate");
  const submitButtonText = `${translate("yes")}, ${confirmAction} ${translate("all").toLocaleLowerCase()}`;

  return (
    <ConfirmationModal
      width="520px"
      open={modalOpened}
      title={`${isInactivating ? translate("inactivateCode") : translate("activateCode")} ${code?.hscode}`}
      type="warning"
      closeModal={onCloseModal}
      submit={onSubmitModal}
      textSubmit={submitButtonText}
      showCancelButton
      leftIconTitle={<VscWarning color="#F79009" size={20} />}
      className={classes.modal}
      saveClassName={classes.confirm}
      cancelClassName={classes.cancel}
      customColorSave={Color.WARNING}
    >
      <Grid container direction="column" wrap="nowrap">
        <Grid item xs={12} className={classNames(classes.text, classes.info)}>
          <span>{isInactivating ? translate("inactivatingASectionChapterOrHeadingAllHSCommodityCodesRelatedWillBeInactivatedAsWell") : translate("activatingASectionChapterOrHeadingAllHSCommodityCodesRelatedWillBeActivatedAsWell")}</span>
        </Grid>
        <Grid item xs={12} className={classNames(classes.text, classes.warning)}>
          <span>
            {isInactivating ? translate("doYouWantToInactivateAllOfThemAsWell") : translate("doYouWantToActivateAllOfThemAsWell")}
          </span>
        </Grid>
      </Grid>
    </ConfirmationModal>
  );
};

export default forwardRef(SwitchWarningModal);
